import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { confirmSignIn, signIn, getCurrentUser, signOut } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { UserInfo, UserInfoResponse } from '../models/user.models';
import { BYPASS_TPI_INTERCEPTOR } from './interceptors';

export interface JwtPayload {
  email: string;
  authChallenge: string;
  meterNumber?: string;
  agentOrgId?: string;
  agentOrg?: string;
  consentRequestId: string;
  agentEmail?: string;
  // Add other fields if necessary
}

export interface CognitoAuthResult {
  success: boolean;
  userDetails?: UserInfo;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private router = inject(Router);
  private http = inject(HttpClient);
  private _loginUser: UserInfo | null = null;
  private _isLoggedIn = new BehaviorSubject<boolean>(false);

  constructor() {
    // Initialize from Cognito session on startup
    this.initializeFromCognito();
  }

  // Getter for loginUser property
  get loginUser(): UserInfo | null {
    return this._loginUser;
  }

  // Getter for loginUserEmail property
  get loginUserEmail(): Observable<string> {
    return from((async () => {
      const currentUser = await getCurrentUser();
      return currentUser?.signInDetails?.loginId || '';
    })());
  }

  get loginUserInfo(): Observable<UserInfo | null> {
    if (this._loginUser) {
      return of(this._loginUser);
    } else {
      return this.loginUserEmail.pipe(
        switchMap(email => {
          if (email) {
            return from(this.getUserDetails(email)).pipe(
              map(userDetails => {
                return userDetails;
              }),
              catchError(() => of(null))
            );
          } else {
            return of(null);
          }
        })
      );
    }
  }

  private async initializeFromCognito(): Promise<void> {
    try {
      const isSignedIn = await this.isCognitoUserSignedIn();
      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        const email = currentUser.signInDetails?.loginId || currentUser.username;
        
        if (email) {
          const userDetails = await this.getUserDetails(email, true);
          this._loginUser = userDetails;
          this._isLoggedIn.next(true);
          console.log('User initialized from Cognito:', userDetails);
        }
      } else {
        this._isLoggedIn.next(false);
      }
    } catch (error) {
      console.error('Error initializing from Cognito:', error);
      this._isLoggedIn.next(false);
    }
  }

  // Method to check if user is logged in
  isLoggedIn(): Observable<boolean> {
    return this._isLoggedIn.asObservable();
  }

  /**
   * Async method to check if user is logged in with proper Cognito verification
   * This should be used by guards and other places that need to wait for auth verification
   * @returns Promise<boolean> indicating if user is authenticated
   */
  async isLoggedInAsync(): Promise<boolean> {
    try {
      return await this.isCognitoUserSignedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  // Method to handle logout
  async logout(redirect = true): Promise<void> {
    try {
      // Sign out from Cognito
      await signOut();
      console.log('Successfully signed out from Cognito');
    } catch (error) {
      console.error('Error signing out from Cognito:', error);
      // Continue with local logout even if Cognito signout fails
    }

  //  localStorage.removeItem('logged_in_email');
    // Clear local state
    this._isLoggedIn.next(false);
    this._loginUser = null;
    
    console.log('User logged out');
    if (redirect) {
      this.router.navigate(['/landing']);
    }
  }

  recordLogin(userDetails: UserInfo): void {
    console.log(`Logging in user: ${userDetails.Firstname} (${userDetails.Email})`);
    this._loginUser = userDetails;
    this._isLoggedIn.next(true);
  }
  /**
   * Sends a magic link to the specified email address
   * @param email The email address to send the magic link to
   * @returns Observable<string | null> indicating whether the magic link was sent successfully
   */
  sendMagicLink(email: string): Observable<string | null> {
    const url = `${environment.baseUrl}/dfs/v1/user-login`;
    
    const payload = {
      email: email,
      subject: environment.loginSubject,
      URL: environment.loginUrl
    };
    
    console.log(`Sending login link to: ${email}`);

    return this.http.post<any>(url, payload).pipe(
      map(response => {
        console.log('Login link sent successfully');
        return response.message || 'Login link sent successfully';
      }),
      catchError(error => {
        console.error('Error sending login link:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetches user details for a specific email using DFS user info
   * @param email The email address of the user
   * @returns Promise<UserInfo> with the user's DFS information
   */
  getUserDetails(email: string, byPassInterceptor = false): Promise<UserInfo> {
    console.log(`Fetching user details for: ${email}`);
    
    const url = `${environment.baseUrl}/dfs/v1/dfs-user-info`;
    let context = new HttpContext();
    if (byPassInterceptor) {
      context = context.set(BYPASS_TPI_INTERCEPTOR, true);
    }
    return firstValueFrom(this.http.get<UserInfoResponse>(url, {
      params: { email },
      context
    }).pipe(
      map(response => {
        // Find the user data for the specific email
        const userData = response.data.find(user => user.Email.toLowerCase() === email.toLowerCase());
        this._loginUser = userData || null;
        if (!userData) {
          throw new Error(`User not found for email: ${email}`);
        }
        
        return userData;
      }),
      catchError(error => {
        console.error('Error fetching user details:', error);
        throw error;
      })
    ));
  }

  // ==================== COGNITO AUTHENTICATION METHODS ====================

  /**
   * Checks if user is already signed in with Cognito
   * @returns Promise<boolean> indicating if user is signed in
   */
  async isCognitoUserSignedIn(): Promise<boolean> {

    // Cognito's getCurrentUser method seems to be not integrated correctly.
    // TODO: Debug this later to see if we can get the current user directly from Cognito.

  //  return localStorage.getItem('logged_in_email') !== null;
    try {
      const currentUser = await getCurrentUser();
      return !!currentUser;
    } catch (error) {
      console.error('Error checking Cognito user status:', error);
      return false;
    }
  }

  /**
   * Decodes the 'link' parameter to extract the JWT payload.
   * @param link The Base64-encoded JWT string.
   * @returns The decoded JWT payload or null if decoding fails.
   */
  decodeAuthLink(link: string): JwtPayload | null {
    try {
      // Base64 decode
      const decodedLink = atob(link);

      // Parse JWT
      const decodedJwt = jwtDecode<JwtPayload>(decodedLink);

      return decodedJwt;
    } catch (error) {
      console.error('Error decoding auth link:', error);
      return null;
    }
  }

  /**
   * Authenticates user using Cognito with email and challenge response
   * @param email The user's email address
   * @param challengeResponse The challenge response from the JWT
   * @returns Promise<CognitoAuthResult> with authentication result
   */
  async authenticateWithCognito(email: string, challengeResponse: any): Promise<CognitoAuthResult> {
    try {
      // Check if user is already signed in
      const isSignedIn = await this.isCognitoUserSignedIn();
      if (isSignedIn) {
        console.log('User is already signed in to Cognito');
        const userDetails = await this.getUserDetails(email, true);
        return { success: true, userDetails };
      }
      
      // Initiate sign-in with custom auth flow
      const { isSignedIn: signInSuccess, nextStep } = await signIn({
        username: email,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          preferredChallenge: challengeResponse
        }
      });

      if (!signInSuccess && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const response = await confirmSignIn({
          challengeResponse
        });

        // Cognito does not seem to return a direct success flag here
        // so we assume if we reach here, the sign-in was successful

        // Also storing the email in the localStorage 
       // localStorage.setItem('logged_in_email', email);

      if (response.isSignedIn) {
          console.log('User signed in successfully with Cognito');
          const userDetails = await this.getUserDetails(email, true);
          this.recordLogin(userDetails);
          return { success: true, userDetails };
        } else {
          return { success: false, error: 'Sign-in confirmation failed' };
        }
      } else if (signInSuccess) {
        const userDetails = await this.getUserDetails(email, true);
        return { success: true, userDetails };
      } else {
        return { success: false, error: 'Sign-in failed' };
      }

    } catch (error: any) {
      console.error('Error during Cognito authentication:', error);

      // Handle specific Cognito errors
      if (error.code === 'UserNotFoundException') {
        return { success: false, error: 'User does not exist in Cognito' };
      } else {
        return { success: false, error: error.message || 'Authentication failed' };
      }
    }
  }

  /**
   * Complete authentication flow - decode link, authenticate with Cognito, and record login
   * @param link The Base64-encoded JWT string from the magic link
   * @returns Promise<CognitoAuthResult> with complete authentication result
   */
  async completeAuthenticationFlow(link: string): Promise<CognitoAuthResult & { 
    redirectInfo?: Partial<JwtPayload>
  }> {
    try {
      // 1. Decode the JWT link
      const decodedJwt = this.decodeAuthLink(link);
      if (!decodedJwt) {
        return { success: false, error: 'Failed to decode authentication link' };
      }

      const { email, authChallenge, consentRequestId} = decodedJwt;
      if (!email) {
        return { success: false, error: 'Email not found in authentication link' };
      }

      if (!authChallenge) {
        return { success: false, error: 'Challenge response not found in authentication link' };
      }

      // 2. Authenticate with Cognito
      const authResult = await this.authenticateWithCognito(email, authChallenge);
      if (!authResult.success || !authResult.userDetails) {
        return authResult;
      }

      // 4. Record the login
      // We will record the login inside authenticateWithCognito method
   //   this.recordLogin(authResult.userDetails);

      // 5. Return result with redirect information
      return {
        success: true,
        userDetails: authResult.userDetails,
        redirectInfo: { consentRequestId }
      };

    } catch (error: any) {
      console.error('Error in complete authentication flow:', error);
      return { success: false, error: error.message || 'SIGN_IN_FAILED' };
    }
  }



  isCompanyUser(): boolean {
    return !!(this.loginUser?.Organization);
  }

  /**
   * Gets TPI status from user info
   * @returns Observable<boolean> indicating if user is TPI
   */
  getTpiStatus(): Observable<boolean> {
   return this.loginUserInfo.pipe(
      map(user => {
        return user?.isTPI === 1;
      })
    );
  }

  /**
   * Gets UTM source from user info
   * @returns Observable<string> UTM source
   */
  getUtmSource(): Observable<string> {
    return this.loginUserInfo.pipe(
      map(user => {
        return user?.UtmSource || '';
      })
    );
  }
}
